import { debounce } from "lodash";
import * as React from "react";
import { FC, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { Form, actions } from "react-redux-form";
import { useParams } from "react-router";
import Alert from "react-s-alert";
import { bindActionCreators } from "redux";
import * as ItemComponentTypeActions from "../../../actions/itemComponentType";
import * as NavigationActions from "../../../actions/navigation";
import * as TrialItemTemplateActions from "../../../actions/trialItemTemplate";
import { NotificationAlert } from "../../../components/common/index";
import { TextAreaFormInput } from "../../../components/form";
import { CheckboxFormInput, NumberFormInput, SelectFormInput, TextFormInput } from "../../../components/form/index";
import { FontAwesomeIcons } from "../../../constants/fontAwesomeIcons";
import { CollectionPage } from "../../../containers/index";
import * as Dtos from "../../../dtos/Tmd.dtos";
import { RequestState } from "../../../enumerations/RequestState";
import { renderRegistrationFormSaveBlock } from "../../../helpers/alert";
import * as AuthenticationHelper from "../../../helpers/authentication";
import { TrialItemTemplateBreadcrumbs, TrialItemTemplateCreateBreadcrumbs } from "../../../helpers/breadcrumbHelper";
import { RegistrationComponent, renderFormControls } from "../../../helpers/formHelper";
import { findLookup, generateOptionsFromLookup } from "../../../helpers/lookupHelper";
import { SettingsTrialSubMenu } from "../../../helpers/subMenuHelper";
import useNavigate from "../../../hooks/useNavigate";
import { RegistrationFormComponent } from "../../../interfaces/forms/IRegistrationComponent";
import { ITmdState } from "../../../reducers/index";
import { IRegistrationFormState } from "../../../reducers/reactReduxForms/index";
import { TrialLookupsLayout } from "../../index";

import "./TrialItemTemplatePage.scss";

interface TrialItemTemplatePageProps {
    form: Dtos.TrialItemTemplate & IRegistrationFormState;
    formState: Dtos.FormState;
    formProperties: Dtos.FormProperty[];
    trialItemTemplate: Dtos.TrialItemTemplate;
    collection: Dtos.Collection;
    lookups: Dtos.Lookup[];
    loadingTrialItemTemplate: boolean;
    loadTrialItemTemplateFailure: boolean;
    loadTrialItemTemplateErrorCode: string;
    savingTrialItemTemplate: boolean;
    saveTrialItemTemplateSuccess: boolean;
    saveTrialItemTemplateFailure: boolean;
    creatingTrialItemTemplate: boolean;
    createTrialItemTemplateSuccess: boolean;
    validatingTrialItemTemplate: boolean;
    validationFailures: Dtos.ResponseError[];
    permissions?: Dtos.PersonnelTmdPermissions[];
    itemComponentTypes: Dtos.ItemComponentType[];
    loadingItemComponentTypes: boolean;
    loadItemComponentTypesSuccess: boolean;
    loadItemComponentTypesFailure: boolean;
    loadItemComponentTypesErrorCode: string;
    clearTrialItemTemplate: TrialItemTemplateActions.ITrialItemTemplateClearActionCreator;
    loadTrialItemTemplate: TrialItemTemplateActions.ITrialItemTemplateLoadByIdActionCreator;
    saveTrialItemTemplate: TrialItemTemplateActions.ITrialItemTemplateSaveActionCreator;
    createTrialItemTemplate: TrialItemTemplateActions.ITrialItemTemplateCreateActionCreator;
    validateTrialItemTemplate: TrialItemTemplateActions.ITrialItemTemplateValidateActionCreator;
    loadForm: typeof actions.load;
    changeForm: typeof actions.change;
    resetForm: typeof actions.reset;
    setBreadcrumbs: NavigationActions.ISetBreadcrumbsActionCreator;
    setDrawerItems: NavigationActions.ISetDrawerItemsActionCreator;
    clearItemComponentTypes: ItemComponentTypeActions.IItemComponentTypeClearActionCreator;
    loadItemComponentTypes: ItemComponentTypeActions.IItemComponentTypeLoadActionCreator;
}

const reduxFormName: string = "reduxForms.trialItemTemplate";

const TrialItemTemplatePage: FC<TrialItemTemplatePageProps> = ({
    form,
    formState,
    formProperties,
    trialItemTemplate,
    collection,
    lookups,
    loadingTrialItemTemplate,
    loadTrialItemTemplateFailure,
    loadTrialItemTemplateErrorCode,
    savingTrialItemTemplate,
    saveTrialItemTemplateSuccess,
    saveTrialItemTemplateFailure,
    creatingTrialItemTemplate,
    createTrialItemTemplateSuccess,
    validatingTrialItemTemplate,
    validationFailures,
    permissions,
    itemComponentTypes,
    loadingItemComponentTypes,
    // loadItemComponentTypesSuccess,
    loadItemComponentTypesFailure,
    loadItemComponentTypesErrorCode,
    clearTrialItemTemplate,
    loadTrialItemTemplate,
    saveTrialItemTemplate,
    createTrialItemTemplate,
    validateTrialItemTemplate,
    loadForm,
    changeForm,
    resetForm,
    setBreadcrumbs,
    setDrawerItems,
    clearItemComponentTypes,
    loadItemComponentTypes
}) => {
    //
    // -- Params --
    const { trialId } = useParams<{trialId: string;}>();
    const trialIdNumber = parseInt(trialId, 10);
    const { trialItemTemplateId } = useParams<{trialItemTemplateId: string;}>();
    const trialItemTemplateIdNumber = parseInt(trialItemTemplateId, 10);

    //
    // -- Hooks --
    const navigate = useNavigate();

    //
    // -- States --
    const [saveAndReturn, setSaveAndReturn] = useState<boolean>(false);
    const [initalised, setInitalised] = useState<boolean>(false);
    const [hasBeenSubmitted, setHasBeenSubmitted] = useState<boolean>(false);

    const loadReduxForm = () => {
        let extraData: Dtos.TrialItemTemplate = {} as Dtos.TrialItemTemplate;

        if (!isNaN(trialIdNumber)) {
            extraData.trialId = trialIdNumber;
        }

        loadForm(
            reduxFormName,
            Object.assign({ ...trialItemTemplate }, extraData)
        );
    };


    // Load trial item template
    useEffect(() => {
        if (
            trialItemTemplateIdNumber &&
            !trialItemTemplate &&
            !loadingTrialItemTemplate
        ) {
            setDrawerItems(SettingsTrialSubMenu(permissions));
            setBreadcrumbs(
                TrialItemTemplateBreadcrumbs(
                    trialItemTemplateIdNumber,
                    isNaN(trialIdNumber) ? undefined : trialIdNumber
                )
            );
            loadTrialItemTemplate(trialItemTemplateIdNumber);
        } else if (
            !trialItemTemplateIdNumber &&
            !creatingTrialItemTemplate &&
            !createTrialItemTemplateSuccess
        ) {
            createTrialItemTemplate(isNaN(trialIdNumber) ? undefined : trialIdNumber);
            setBreadcrumbs(
                TrialItemTemplateCreateBreadcrumbs(isNaN(trialIdNumber) ? undefined : trialIdNumber)
            );
            setDrawerItems([]);
        } else if (trialItemTemplate && !initalised) {
            loadReduxForm();
            setInitalised(true);
        }

        if (!itemComponentTypes && !loadingItemComponentTypes) {
            loadItemComponentTypes();
        }
    }, [
        trialItemTemplateIdNumber,
        trialItemTemplate,
        setDrawerItems,
        SettingsTrialSubMenu,
        permissions,
        setBreadcrumbs,
        TrialItemTemplateBreadcrumbs,
        loadTrialItemTemplate,
        createTrialItemTemplate,
        initalised,
        setInitalised,
        loadReduxForm,
        loadingTrialItemTemplate,
        creatingTrialItemTemplate,
        createTrialItemTemplateSuccess,
        TrialItemTemplateCreateBreadcrumbs,
        itemComponentTypes,
        loadingItemComponentTypes,
        loadItemComponentTypes,
    ]);

    // Navigate to error page on load failure
    useEffect(() => {
        if (loadTrialItemTemplateFailure) {
            navigate(`/error/${loadTrialItemTemplateErrorCode}`);
        }
        if (loadItemComponentTypesFailure) {
            navigate(`/error/${loadItemComponentTypesErrorCode}`);
        }
    }, [
        loadTrialItemTemplateFailure,
        loadTrialItemTemplateErrorCode,
        loadItemComponentTypesFailure,
        loadItemComponentTypesErrorCode,
        navigate,
    ]);

    // Update breadcrumbs
    useEffect(() => {
        if (trialItemTemplateIdNumber && trialItemTemplate?.name) {
            setBreadcrumbs(
                TrialItemTemplateBreadcrumbs(
                    trialItemTemplateIdNumber,
                    isNaN(trialIdNumber) ? undefined : trialIdNumber,
                    trialItemTemplate?.name
                )
            );
        }
    }, [
        trialItemTemplateIdNumber,
        trialItemTemplate,
        setBreadcrumbs,
        TrialItemTemplateBreadcrumbs,
    ]);

    // Reload trial item template after saving
    useEffect(() => {
        if (!savingTrialItemTemplate && saveTrialItemTemplateSuccess) {
            Alert.success(
                <NotificationAlert
                    alertContent={<span>Trial item template saved successfully</span>}
                    icon={FontAwesomeIcons.Solid.CHECK}
                />
            );

            if (saveAndReturn) {
                let url: string = trialIdNumber
                    ? `/settings/trial/itemtemplates/${trialIdNumber}`
                    : "/settings/trial/itemtemplates";
                navigate(url);
            } else if (!trialItemTemplateIdNumber) {
                navigate(`/settings/trial/itemtemplates/${trialItemTemplate?.trialId}/template/${trialItemTemplate?.id}`);
                setInitalised(false);
                clearTrialItemTemplate();
            }
        }
    }, [
        savingTrialItemTemplate,
        saveTrialItemTemplateSuccess
    ]);

    // Failed saving
    useEffect(() => {
        if (!savingTrialItemTemplate && saveTrialItemTemplateFailure) {
            Alert.error(
                <NotificationAlert
                    alertContent={renderRegistrationFormSaveBlock()}
                    icon={FontAwesomeIcons.Solid.BAN}
                />
            );
        }
    }, [savingTrialItemTemplate, saveTrialItemTemplateFailure]);

    // Cleanup effect to clear the trial item template on component unmount
    useEffect(() => {
        return () => {
            clearTrialItemTemplate();
            clearItemComponentTypes();
            resetForm(reduxFormName);
        };
    }, [
        clearTrialItemTemplate,
        clearItemComponentTypes,
        resetForm,
        reduxFormName,
    ]);

    const onCancel = () => {
        let url: string = trialIdNumber
            ? `/settings/trial/itemtemplates/${trialIdNumber}`
            : "/settings/trial/itemtemplates";
        navigate(url);
    };

    const handleOnSubmitFailed = (_data: Dtos.TrialItemTemplate) => {
        Alert.error(
            <NotificationAlert
                alertContent={renderRegistrationFormSaveBlock()}
                icon={FontAwesomeIcons.Solid.BAN}
            />
        );
    };

    const handleOnSubmit = async (data: any) => {
        if (!hasBeenSubmitted) {
            setHasBeenSubmitted(true);
        }

        if (data) {
            let changes: Dtos.TrialItemTemplate = {} as Dtos.TrialItemTemplate;
            let collectionChanges: Dtos.Collection = {} as Dtos.Collection;

            if (!data?.allowIntraVersions) {
                changes.initialVersion = null;
            }

            await saveTrialItemTemplate(
                Object.assign({ ...data }, changes),
                Object.assign({ ...collection }, collectionChanges)
            );

            if (data.saveAndReturn) {
                setSaveAndReturn(true);
            }
        }
    };

    // Debounce validation after the initial submit to clear form validation errors as the form get updated.
    useEffect(() => {
        const debounceValidate = debounce(async () => {
            let changes: Dtos.TrialItemTemplate = {} as Dtos.TrialItemTemplate;
            let collectionChanges: Dtos.Collection = {} as Dtos.Collection;

            if (!form?.allowIntraVersions) {
                changes.initialVersion = null;
            }

            if (form && hasBeenSubmitted && !validatingTrialItemTemplate) {
                await validateTrialItemTemplate(
                    Object.assign({ ...form }, changes),
                    Object.assign({ ...collection }, collectionChanges)
                );
            }
        }, 250);

        debounceValidate();

        return () => {
            debounceValidate.cancel();
        };
    }, [form]);

    const renderCollection = useMemo(() => {
        const hasPermission: boolean =
            AuthenticationHelper.hasCreateEditPermission(
                form,
                permissions,
                Dtos.Permission.TrialItemUpdate,
                Dtos.Permission.TrialItemCreate
            );
        const formDisabled: boolean =
            !hasPermission ||
            savingTrialItemTemplate ||
            (formState && formState.formStatus == Dtos.FormStatus.Disabled);

        if (form && !loadingTrialItemTemplate && itemComponentTypes) {
            return (
                <CollectionPage
                    itemComponentTypeId={
                        itemComponentTypes?.find((x) => x.value === "Trial")?.id
                    }
                    collectionId={form.collectionId || 0}
                    readonly={formDisabled}
                />
            );
        }
    }, [
        form,
        permissions,
        savingTrialItemTemplate,
        formState,
        loadingTrialItemTemplate,
        itemComponentTypes,
    ]);

    const renderContent = useMemo(() => {
        const hasPermission: boolean =
            AuthenticationHelper.hasCreateEditPermission(
                form,
                permissions,
                Dtos.Permission.TrialItemUpdate,
                Dtos.Permission.TrialItemCreate
            );
        const formDisabled: boolean =
            !hasPermission ||
            savingTrialItemTemplate ||
            (formState && formState.formStatus == Dtos.FormStatus.Disabled);

        let formComponents: { [index: string]: RegistrationFormComponent } = {
            details: {
                type: RegistrationComponent.RegistrationElement,
                component: (
                    <div className="card card-shadow">
                        <div className="form-row pt-4 pb-2 px-4">
                            <div className="col-12">
                                {renderFormControls(
                                    form,
                                    "trialItemTemplate",
                                    {
                                        name: {
                                            type: RegistrationComponent.RegistrationControl,
                                            inputType: TextFormInput,
                                            inputDisabled: formDisabled,
                                            groupClassName: "form-row bottom-0 border-top-0 pb-0 pt-0",
                                            labelClassName: "col-form-label col-sm-12 col-md-2 col-xl-1",
                                            fieldClassName: "col-sm-12 col-md-10 col-xl-11 generic-form-field mr-0 pr-0",
                                        },
                                    },
                                    lookups,
                                    validationFailures,
                                    formProperties,
                                    changeForm
                                )}
                            </div>
                        </div>
                        <div className="form-row py-2 px-4">
                            <div className="col-12">
                                {renderFormControls(
                                    form,
                                    "trialItemTemplate",
                                    {
                                        description: {
                                            type: RegistrationComponent.RegistrationControl,
                                            inputType: TextAreaFormInput,
                                            inputDisabled: formDisabled,
                                            inputProps: {
                                                rows: 3,
                                            },
                                            groupClassName: "form-row bottom-0 border-top-0 pb-0 pt-0",
                                            labelClassName: "col-form-label col-sm-12 col-md-2 col-xl-1",
                                            fieldClassName: "col-sm-12 col-md-10 col-xl-11 generic-form-field mr-0 pr-0",
                                        },
                                    },
                                    lookups,
                                    validationFailures,
                                    formProperties,
                                    changeForm
                                )}
                            </div>
                        </div>
                        <div className="form-row py-2 px-4">
                            <div className="col-lg-12 col-xl-6">
                                {renderFormControls(
                                    form,
                                    "trialItemTemplate",
                                    {
                                        categoryId: {
                                            type: RegistrationComponent.RegistrationControl,
                                            inputType: SelectFormInput,
                                            inputDisabled: formDisabled,
                                            inputProps: {
                                                options:
                                                    generateOptionsFromLookup(
                                                        findLookup(
                                                            "TrialItemTemplate",
                                                            "CategoryId",
                                                            lookups
                                                        ),
                                                        form.categoryId
                                                    ),
                                            },
                                            groupClassName: "form-row bottom-0 border-top-0 pb-sm-0 pb-md-2 pb-xl-0",
                                            labelClassName: "col-form-label col-sm-12 col-md-2",
                                            fieldClassName: "col-sm-12 col-md-10 col-xl-9 generic-form-field mr-0 pr-0",
                                        },
                                    },
                                    lookups,
                                    validationFailures,
                                    formProperties,
                                    changeForm
                                )}
                            </div>
                            <div className="col-lg-12 col-xl-6">
                                {renderFormControls(
                                    form,
                                    "trialItemTemplate",
                                    {
                                        rank: {
                                            type: RegistrationComponent.RegistrationControl,
                                            inputType: NumberFormInput,
                                            inputDisabled: formDisabled,
                                            groupClassName: "form-row bottom-0 border-top-0 pt-sm-0 pt-md-2 pt-xl-0",
                                            labelClassName: "col-form-label col-sm-12 col-md-2",
                                            fieldClassName: "col-sm-12 col-md-10 generic-form-field mr-0 pr-0",
                                        },
                                    },
                                    lookups,
                                    validationFailures,
                                    formProperties,
                                    changeForm
                                )}
                            </div>
                        </div>
                        <div className="form-row py-2 px-4">
                            <div className="col-12">
                                {renderFormControls(
                                    form,
                                    "trialItemTemplate",
                                    {
                                        trialId: {
                                            type: RegistrationComponent.RegistrationControl,
                                            inputType: SelectFormInput,
                                            inputDisabled: formDisabled || !isNaN(trialIdNumber),
                                            inputProps: {
                                                options:
                                                    generateOptionsFromLookup(
                                                        findLookup(
                                                            "TrialItemTemplate",
                                                            "TrialId",
                                                            lookups
                                                        ),
                                                        form.trialId
                                                    ),
                                                clearable: true,
                                            },
                                            groupClassName: "form-row bottom-0 border-top-0 pb-sm-0 pb-md-2 pb-xl-0",
                                            labelClassName: "col-form-label col-sm-12 col-md-2 col-xl-1",
                                            fieldClassName: "col-sm-12 col-md-10 col-xl-11 generic-form-field mr-0 pr-0",
                                        },
                                    },
                                    lookups,
                                    validationFailures,
                                    formProperties,
                                    changeForm
                                )}
                            </div>
                        </div>
                        <div className="form-row py-2 px-4">
                            <div
                                className="col-lg-12 col-xl-6"
                                style={{ display: "grid" }}
                            >
                                {renderFormControls(
                                    form,
                                    "trialItemTemplate",
                                    {
                                        allowIntraVersions: {
                                            type: RegistrationComponent.RegistrationControl,
                                            inputType: CheckboxFormInput,
                                            inputDisabled: formDisabled,
                                            inputProps: {
                                                label: "Allow intra versions",
                                            },
                                            inputLabel: " ",
                                            groupClassName: "form-row bottom-0 border-top-0 pb-0 pt-0 pb-sm-0 pb-md-2 pb-xl-0 align-items-center",
                                            labelClassName: "col-form-label col-sm-12 col-md-2",
                                            fieldClassName: "col-sm-12 col-md-10 col-xl-9 generic-form-field",
                                            changeAction: (_model, value, _form, _parentModel) => {
                                                changeForm(`${reduxFormName}.allowIntraVersions`, value);
                                                if (!value) {
                                                    changeForm(`${reduxFormName}.initialVersion`, null);
                                                }
                                            },
                                        },
                                    },
                                    lookups,
                                    validationFailures,
                                    formProperties,
                                    changeForm
                                )}
                            </div>
                            <div className="col-lg-12 col-xl-6">
                                {renderFormControls(
                                    form,
                                    "trialItemTemplate",
                                    {
                                        initialVersion: {
                                            type: RegistrationComponent.RegistrationControl,
                                            inputType: TextFormInput,
                                            inputDisabled: formDisabled || !form.allowIntraVersions,
                                            groupClassName: "form-row bottom-0 border-top-0 pb-0 pt-0",
                                            labelClassName: "col-form-label col-sm-12 col-md-2",
                                            fieldClassName: "col-sm-12 col-md-10 generic-form-field mr-0 pr-0",
                                        },
                                    },
                                    lookups,
                                    validationFailures,
                                    formProperties,
                                    changeForm
                                )}
                            </div>
                        </div>
                        <div className="form-row pt-2 pb-4 px-4">
                            <div
                                className="col-lg-12 col-xl-6"
                                style={{ display: "grid" }}
                            >
                                {renderFormControls(
                                    form,
                                    "trialItemTemplate",
                                    {
                                        allowVersionChanges: {
                                            type: RegistrationComponent.RegistrationControl,
                                            inputType: CheckboxFormInput,
                                            inputDisabled: formDisabled,
                                            inputProps: {
                                                label: "Allow version label changes",
                                            },
                                            inputLabel: " ",
                                            groupClassName: "form-row bottom-0 border-top-0 pb-0 pt-0 pb-sm-0 pb-md-2 pb-xl-0 align-items-center",
                                            labelClassName: "col-form-label col-sm-12 col-md-2",
                                            fieldClassName: "col-sm-12 col-md-10 col-xl-9 generic-form-field",
                                        },
                                    },
                                    lookups,
                                    validationFailures,
                                    formProperties,
                                    changeForm
                                )}
                            </div>
                            <div
                                className="col-lg-12 col-xl-6"
                                style={{ display: "grid" }}
                            >
                                {renderFormControls(
                                    form,
                                    "trialItemTemplate",
                                    {
                                        active: {
                                            type: RegistrationComponent.RegistrationControl,
                                            inputType: CheckboxFormInput,
                                            inputDisabled: formDisabled,
                                            inputProps: {
                                                label: "Active",
                                            },
                                            inputLabel: " ",
                                            groupClassName: "form-row bottom-0 border-top-0 pb-0 pt-0 pt-sm-0 pt-md-2 pt-xl-0 align-items-center",
                                            labelClassName: "col-form-label col-sm-12 col-md-2",
                                            fieldClassName: "col-sm-12 col-md-10 generic-form-field",
                                        },
                                    },
                                    lookups,
                                    validationFailures,
                                    formProperties,
                                    changeForm
                                )}
                            </div>
                        </div>
                    </div>
                ),
            },
            components: {
                type: RegistrationComponent.RegistrationElement,
                component: (
                    <>
                        <br />
                        <h1>Components</h1>
                        {renderCollection}
                    </>
                ),
            },
        };

        return (
            <div>
                <h1>Trial Item Template</h1>
                <Form
                    model={reduxFormName}
                    onSubmit={(val, _event) => handleOnSubmit(val)}
                    onSubmitFailed={(val) => handleOnSubmitFailed(val)}
                >
                    {renderFormControls(
                        form,
                        "trialItemTemplate",
                        formComponents,
                        lookups,
                        validationFailures,
                        formProperties,
                        changeForm
                    )}
                    <div className="mt-4 mb-4 text-right">
                        <button
                            disabled={formDisabled}
                            onClick={() => {
                                changeForm(`${reduxFormName}.saveAndReturn`, false);
                            }}
                            type="submit"
                            className="btn btn-primary mr-2"
                        >
                            Submit
                        </button>

                        <button
                            disabled={formDisabled}
                            onClick={() => {
                                changeForm(`${reduxFormName}.saveAndReturn`, true);
                            }}
                            type="submit"
                            className="btn btn-primary mr-2"
                        >
                            Submit And Return
                        </button>
                        <button
                            onClick={onCancel}
                            className="btn btn-primary mr-2"
                        >
                            Cancel
                        </button>
                    </div>
                </Form>
            </div>
        );
    }, [
        form,
        permissions,
        savingTrialItemTemplate,
        formState,
        lookups,
        generateOptionsFromLookup,
        findLookup,
        renderCollection,
        reduxFormName,
        handleOnSubmit,
        handleOnSubmitFailed,
        validationFailures,
        formProperties,
        changeForm,
        onCancel,
        trialIdNumber,
    ]);

    return (
        <TrialLookupsLayout
            loading={loadingTrialItemTemplate || creatingTrialItemTemplate}
            permissions={permissions}
            permission={Dtos.Permission.TrialItemView}
        >
            {renderContent}
        </TrialLookupsLayout>
    );
};

const mapStateToProps = (state: ITmdState): Partial<TrialItemTemplatePageProps> => {
    let trialItemTemplate: Dtos.TrialItemTemplate | undefined = 
        !(state.trialItemTemplate.formData instanceof Array)
            ? state.trialItemTemplate.formData
            : undefined;
    let collection: Dtos.Collection | undefined = 
        !(state.reduxForms.collection instanceof Array)
            ? state.reduxForms.collection
            : undefined;
    let permissions = state.user.userSummary? state.user.userSummary.permissions : [];
    let itemComponentTypes = state.itemComponentTypes.formData instanceof Array
        ? state.itemComponentTypes.formData
        : undefined;

    return {
        trialItemTemplate: trialItemTemplate!,
        collection: collection!,
        loadingTrialItemTemplate:
            state.trialItemTemplate.loadState &&
            state.trialItemTemplate.loadState.status == RequestState.Pending,
        loadTrialItemTemplateFailure:
            state.trialItemTemplate.loadState &&
            state.trialItemTemplate.loadState.status == RequestState.Failure,
        loadTrialItemTemplateErrorCode: state.trialItemTemplate.loadState.errorCode,
        savingTrialItemTemplate:
            state.trialItemTemplate.saveState &&
            state.trialItemTemplate.saveState.status == RequestState.Pending,
        saveTrialItemTemplateSuccess:
            state.trialItemTemplate.saveState &&
            state.trialItemTemplate.saveState.status == RequestState.Success,
        saveTrialItemTemplateFailure:
            state.trialItemTemplate.saveState &&
            state.trialItemTemplate.saveState.status == RequestState.Failure,
        creatingTrialItemTemplate:
            state.trialItemTemplate.createState &&
            state.trialItemTemplate.createState.status == RequestState.Pending,
        createTrialItemTemplateSuccess:
            state.trialItemTemplate.createState &&
            state.trialItemTemplate.createState.status == RequestState.Success,
        validatingTrialItemTemplate:
            state.trialItemTemplate.validateState &&
            state.trialItemTemplate.validateState.status == RequestState.Pending,
        permissions: permissions,
        lookups: state.trialItemTemplate.lookups,
        form: state.reduxForms.trialItemTemplate,
        formState: state.trialItemTemplate.formState,
        formProperties: state.trialItemTemplate.formProperties,
        validationFailures: trialItemTemplate
            ? state.trialItemTemplate.validationFailures
            : undefined,
        itemComponentTypes: itemComponentTypes,
        loadingItemComponentTypes:
            state.itemComponentTypes.loadState &&
            state.itemComponentTypes.loadState.status == RequestState.Pending,
        loadItemComponentTypesSuccess:
            state.itemComponentTypes.loadState &&
            state.itemComponentTypes.loadState.status == RequestState.Success,
        loadItemComponentTypesFailure:
            state.itemComponentTypes.loadState &&
            state.itemComponentTypes.loadState.status == RequestState.Failure,
        loadItemComponentTypesErrorCode: state.itemComponentTypes.loadState.errorCode,
    };
};

const mapDispatchToProps = (dispatch): Partial<TrialItemTemplatePageProps> => {
    return {
        loadForm: bindActionCreators(actions.load, dispatch),
        changeForm: bindActionCreators(actions.change, dispatch),
        resetForm: bindActionCreators(actions.reset, dispatch),
        loadTrialItemTemplate: bindActionCreators(TrialItemTemplateActions.LoadTrialItemTemplateById, dispatch),
        clearTrialItemTemplate: bindActionCreators(TrialItemTemplateActions.Clear, dispatch),
        saveTrialItemTemplate: bindActionCreators(TrialItemTemplateActions.SaveTrialItemTemplate, dispatch),
        createTrialItemTemplate: bindActionCreators(TrialItemTemplateActions.CreateTrialItemTemplate, dispatch),
        validateTrialItemTemplate: bindActionCreators(TrialItemTemplateActions.ValidateTrialItemTemplate, dispatch),
        setBreadcrumbs: bindActionCreators(NavigationActions.SetBreadcrumbs, dispatch),
        setDrawerItems: bindActionCreators(NavigationActions.SetDrawerItems, dispatch),
        loadItemComponentTypes: bindActionCreators(ItemComponentTypeActions.LoadItemComponentType, dispatch),
        clearItemComponentTypes: bindActionCreators(ItemComponentTypeActions.Clear, dispatch)
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(TrialItemTemplatePage);
